import { memo, useState } from "react";
import { SMS_SUMMARY_FILTER } from "../utilities/constants";
import "../styles/textmessagesummary.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Maps raw summary API data (handles both camelCase and PascalCase keys). */
function buildSummaryData(summary) {
  return {
    sent: summary?.sentCount ?? summary?.SentCount ?? 0,
    delivered: summary?.deliveredCount ?? summary?.DeliveredCount ?? 0,
    pending: summary?.pendingCount ?? summary?.PendingCount ?? 0,
    failed: summary?.failedCount ?? summary?.FailedCount ?? 0,
    optOut: summary?.optedOutCount ?? summary?.OptedOutCount ?? 0,
  };
}

/** Builds the ordered tile descriptor array from derived summary data. */
function buildTiles({ sent, delivered, pending, failed, optOut }) {
  const pctOfTotal = (n) =>
    sent > 0 ? `${((n / sent) * 100).toFixed(0)}% of Total` : "—";
  const bouncePct =
    sent > 0 ? `${((failed / sent) * 100).toFixed(0)}% Bounce Rate` : "—";

  return [
    {
      title: "Sent",
      filterKey: SMS_SUMMARY_FILTER.SENT,
      value: sent,
      subtitle: null,
      color: "var(--fill-success-default, #43A35C)",
    },
    {
      title: "Delivered",
      filterKey: SMS_SUMMARY_FILTER.DELIVERED,
      value: delivered,
      subtitle: pctOfTotal(delivered),
      color: "var(--fill-success-default, #43A35C)",
    },
    {
      title: "Pending",
      filterKey: SMS_SUMMARY_FILTER.PENDING,
      value: pending,
      subtitle: pctOfTotal(pending),
      color: "var(--fill-alert-default, #E67337)",
    },
    {
      title: "Failed",
      filterKey: SMS_SUMMARY_FILTER.FAILED,
      value: failed,
      subtitle: bouncePct,
      color: "var(--fill-danger-default, #D04A4A)",
    },
    {
      title: "Opted Out",
      filterKey: SMS_SUMMARY_FILTER.OPT_OUT,
      value: optOut,
      subtitle: pctOfTotal(optOut),
      color: "var(--fill-danger-default, #D04A4A)",
    },
  ];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SmsTile({ tile, isSelected, onClick }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") onClick();
  };

  return (
    <div
      key={tile.filterKey}
      className={`sms-summary-tile-wrapper${isSelected ? " selected" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
      aria-pressed={isSelected}
    >
      <div className="sms-summary-tile">
        <div className="sms-summary-tile-title">{tile?.title}</div>
        <div className="sms-summary-tile-body">
          <div
            className="sms-summary-tile-value"
            style={{ color: tile?.color }}
          >
            {tile?.value}
          </div>
          {tile?.subtitle && (
            <div className="sms-summary-tile-subtitle">{tile?.subtitle}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function TextMessageSummary({ summary, onFilterChange }) {
  const [selectedTile, setSelectedTile] = useState(null);

  const handleTileClick = (filterKey) => {
    setSelectedTile(filterKey);
    onFilterChange?.(filterKey);
  };

  const tiles = buildTiles(buildSummaryData(summary));

  return (
    <div className="sms-summary-container">
      {tiles.map((tile) => (
        <SmsTile
          key={tile.filterKey}
          tile={tile}
          isSelected={selectedTile === tile.filterKey}
          onClick={() => handleTileClick(tile.filterKey)}
        />
      ))}
    </div>
  );
}

export default memo(TextMessageSummary);
